import {
  CustomProcedure,
  organizationService,
} from "../services/organizationService";

export interface OrganizationCdtCode {
  code: string;
  description: string;
  fee: number;
  durationMinutes: number;
  needsTooth?: boolean;
  needsSurface?: boolean;
  needsRange?: boolean;
  treatArea?: number;
}

export interface OrganizationProcedureData {
  customProcedures: CustomProcedure[];
  internalClinicCodes: CustomProcedure[];
  cdtCodes: OrganizationCdtCode[];
}

const INTERNAL_PROCEDURES_CATEGORY = "Internal Procedures";

let cachedData: OrganizationProcedureData | null = null;
let fetchPromise: Promise<OrganizationProcedureData> | null = null;

function toCustomProcedure(proc: {
  customProcedureId: number;
  cdtCodeId: number;
  codes: string[];
  name: string;
  description: string;
  fee: number;
  durationMinutes: number;
}): CustomProcedure | null {
  const id = proc.customProcedureId || proc.cdtCodeId;
  if (!id || proc.codes.length === 0) return null;

  return {
    id,
    cdtCodeId: proc.cdtCodeId,
    codes: proc.codes,
    name: proc.name,
    description: proc.description,
    fee: proc.fee,
    durationMinutes: proc.durationMinutes,
  };
}

function toCdtCode(proc: {
  codes: string[];
  description: string;
  fee: number;
  durationMinutes: number;
  needsTooth?: boolean;
  needsSurface?: boolean;
  needsRange?: boolean;
  treatArea?: number;
}): OrganizationCdtCode | null {
  if (!proc.codes[0]) return null;
  return {
    code: proc.codes[0],
    description: proc.description ?? "",
    fee: proc.fee ?? 0,
    durationMinutes: proc.durationMinutes ?? 0,
    needsTooth: proc.needsTooth ?? false,
    needsSurface: proc.needsSurface ?? false,
    needsRange: proc.needsRange ?? false,
    treatArea: proc.treatArea,
  };
}

function parseOrganizationProcedures(
  categories: Awaited<ReturnType<typeof organizationService.getProcedures>>,
): OrganizationProcedureData {
  const customProcedures: CustomProcedure[] = [];
  const internalClinicCodes: CustomProcedure[] = [];
  const cdtCodes: OrganizationCdtCode[] = [];

  categories.forEach((category) => {
    const isInternalCategory =
      category.category === INTERNAL_PROCEDURES_CATEGORY;

    category.procedures.forEach((proc) => {
      if (isInternalCategory) {
        const internalProcedure = toCustomProcedure(proc);
        if (internalProcedure) {
          internalClinicCodes.push(internalProcedure);
        }
        return;
      }

      if (proc.customProcedureId) {
        customProcedures.push({
          id: proc.customProcedureId,
          cdtCodeId: proc.cdtCodeId,
          codes: proc.codes,
          name: proc.name,
          description: proc.description,
          fee: proc.fee,
          durationMinutes: proc.durationMinutes,
        });
        return;
      }

      const cdtCode = toCdtCode(proc);
      if (cdtCode) {
        cdtCodes.push(cdtCode);
      }
    });
  });

  return { customProcedures, internalClinicCodes, cdtCodes };
}

export function getOrganizationProcedures(): Promise<OrganizationProcedureData> {
  if (cachedData) {
    return Promise.resolve(cachedData);
  }

  if (!fetchPromise) {
    fetchPromise = organizationService
      .getProcedures()
      .then((categories) => {
        cachedData = parseOrganizationProcedures(categories);
        return cachedData;
      })
      .catch((error) => {
        fetchPromise = null;
        throw error;
      });
  }

  return fetchPromise;
}

export function clearOrganizationProceduresCache(): void {
  cachedData = null;
  fetchPromise = null;
}

/** Sync peek at cached CDT flags (empty if cache not warmed). */
export function peekOrganizationCdtCodes(): OrganizationCdtCode[] {
  return cachedData?.cdtCodes ?? [];
}
